<template>
  <div class="min-h-screen bg-gray-50 text-gray-800">
    <!-- Header -->
    <header class="flex justify-between items-center bg-gray-900 text-white p-4 shadow">
      <h1 class="text-2xl font-bold">Admin Dashboard</h1>
      <button
          @click="logout"
          class="bg-red-500 hover:bg-red-600 px-4 py-2 rounded text-white font-semibold"
      >
        Logout
      </button>
    </header>

    <main class="p-6">
      <!-- Section Buttons -->
      <div class="flex justify-center gap-4 mb-8">
        <button
            v-for="section in sections"
            :key="section.key"
            @click="activeSection = section.key"
            :class="[
            'px-4 py-2 rounded font-semibold',
            activeSection === section.key
              ? 'bg-blue-600 text-white'
              : 'bg-gray-300 text-gray-800 hover:bg-gray-400'
          ]"
        >
          {{ section.label }}
        </button>
      </div>

      <!-- Section Content -->
      <div
          v-if="activeSection"
          class="max-w-6xl mx-auto bg-white shadow-md rounded-lg p-6"
      >
        <h2 class="text-xl font-semibold mb-4">
          {{ sectionLabel(activeSection) }} Management
        </h2>

        <!-- Add New Item -->
        <form
            @submit.prevent="addItem(activeSection)"
            class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
        >
          <input v-model="newItem.title" placeholder="Title" class="p-2 border rounded" required />

          <!-- description only for image sections -->
          <input
              v-if="activeSection !== 'movies'"
              v-model="newItem.description"
              placeholder="Description"
              class="p-2 border rounded"
              required
          />

          <!-- movies extra fields -->
          <template v-if="activeSection === 'movies'">
            <input v-model="newItem.genre" placeholder="Genre" class="p-2 border rounded" required />
            <input v-model="newItem.type" placeholder="Type" class="p-2 border rounded" required />
            <input v-model="newItem.year" placeholder="Year" class="p-2 border rounded" required />
            <input v-model="newItem.url" placeholder="URL" class="p-2 border rounded" />
          </template>

          <input type="file" @change="onFileChange" class="p-2 border rounded" />

          <button
              type="submit"
              class="md:col-span-3 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Add New {{ sectionLabel(activeSection) }}
          </button>
        </form>

        <!-- Table -->
        <div v-if="items[activeSection]" class="overflow-x-auto">
          <table class="min-w-full border-collapse border border-gray-300 rounded-lg">
            <thead class="bg-gray-200">
            <tr>
              <th class="border border-gray-300 p-2">≡</th>
              <th class="border border-gray-300 p-2">Title</th>
              <th class="border border-gray-300 p-2" v-if="activeSection !== 'movies'">Description</th>
              <!-- movie columns -->
              <th class="border border-gray-300 p-2" v-if="activeSection === 'movies'">Genre</th>
              <th class="border border-gray-300 p-2" v-if="activeSection === 'movies'">Type</th>
              <th class="border border-gray-300 p-2" v-if="activeSection === 'movies'">Year</th>
              <th class="border border-gray-300 p-2" v-if="activeSection === 'movies'">URL</th>

              <th class="border border-gray-300 p-2">Image</th>
              <th class="border border-gray-300 p-2">Actions</th>
            </tr>
            </thead>

            <draggable
                v-model="items[activeSection]"
                item-key="id"
                handle=".drag-handle"
                tag="tbody"
                @end="onDragEnd(activeSection)"
            >
              <template #item="{ element: item, index }">
                <tr class="hover:bg-gray-50">
                  <td class="border border-gray-300 p-2 text-center drag-handle cursor-move">≡</td>

                  <td class="border border-gray-300 p-2">
                    <input
                        v-model="item.title"
                        class="border p-1 w-full rounded"
                        @blur="updateItem(activeSection, item)"
                    />
                  </td>

                  <td v-if="activeSection !== 'movies'" class="border border-gray-300 p-2">
                    <input
                        v-model="item.description"
                        class="border p-1 w-full rounded"
                        @blur="updateItem(activeSection, item)"
                    />
                  </td>

                  <!-- movies editable columns -->
                  <td v-if="activeSection === 'movies'" class="border border-gray-300 p-2">
                    <input
                        v-model="item.genre"
                        class="border p-1 w-full rounded"
                        @blur="updateItem(activeSection, item)"
                    />
                  </td>
                  <td v-if="activeSection === 'movies'" class="border border-gray-300 p-2">
                    <input
                        v-model="item.type"
                        class="border p-1 w-full rounded"
                        @blur="updateItem(activeSection, item)"
                    />
                  </td>
                  <td v-if="activeSection === 'movies'" class="border border-gray-300 p-2">
                    <input
                        v-model="item.year"
                        class="border p-1 w-full rounded"
                        @blur="updateItem(activeSection, item)"
                    />
                  </td>
                  <td v-if="activeSection === 'movies'" class="border border-gray-300 p-2">
                    <input
                        v-model="item.url"
                        placeholder="https://..."
                        class="border p-1 w-full rounded"
                        @blur="updateItem(activeSection, item)"
                    />
                  </td>

                  <td class="border border-gray-300 p-2 text-center">
                    <img :src="item.imageurl" alt="preview" class="w-20 h-20 object-cover rounded mb-2 mx-auto" />
                    <input type="file" @change="e => updateImage(e, activeSection, item)" class="block mx-auto" />
                  </td>

                  <td class="border border-gray-300 p-2 text-center flex flex-col gap-1 items-center">
                    <button
                        @click="moveItem(activeSection, index, -1)"
                        :disabled="index === 0"
                        class="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                    >↑</button>
                    <button
                        @click="moveItem(activeSection, index, 1)"
                        :disabled="index === items[activeSection].length - 1"
                        class="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                    >↓</button>
                    <button
                        @click="deleteItem(activeSection, item.id)"
                        class="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 mt-1"
                    >✕</button>
                  </td>
                </tr>
              </template>
            </draggable>
          </table>
        </div>

        <div v-else class="text-center py-6 text-gray-500">
          Loading {{ activeSection }}...
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios'
import draggable from 'vuedraggable'

export default {
  name: 'AdminDashboard',
  components: { draggable },
  data() {
    return {
      sections: [
        { key: 'hair', label: 'Hair' },
        { key: 'makeup', label: 'Makeup' },
        { key: 'sfx', label: 'SFX' },
        { key: 'behind-scenes', label: 'Behind the Scenes' },
        { key: 'movies', label: 'Movies' },
      ],
      activeSection: null,
      items: {},
      newItem: { title: '', description: '', genre: '', type: '', year: '', url: '', image: null },
    }
  },
  methods: {
    handleAxiosError(err) {
      if (err.response && err.response.status === 403) {
        alert('Access forbidden. Redirecting to /admin.')
        this.$router.push('/admin')
      } else {
        console.error(err)
      }
    },

    async fetchItems(section) {
      try {
        const { data } = await axios.get(`/api/${section}`)
        this.$set ? this.$set(this.items, section, data) : (this.items[section] = data)
      } catch (err) {
        this.handleAxiosError(err)
        console.error(`Failed to fetch ${section}:`, err)
      }
    },

    async addItem(section) {
      // --- client-side validation for movies ---
      if (section === 'movies' && (!this.newItem.title || !this.newItem.genre || !this.newItem.type || !this.newItem.year)) {
        alert('Please fill all required fields for movies.')
        return
      }

      const formData = new FormData()
      formData.append('title', this.newItem.title)

      if (section === 'movies') {
        formData.append('genre', this.newItem.genre)
        formData.append('type', this.newItem.type)
        formData.append('year', this.newItem.year ? parseInt(this.newItem.year) : null) // integer cast
        formData.append('url', this.newItem.url ? this.newItem.url : null) // normalize empty URL to null
      } else {
        formData.append('description', this.newItem.description)
      }

      if (this.newItem.image) formData.append('image', this.newItem.image)

      try {
        const token = localStorage.getItem('token')
        await axios.post(`/api/${section}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        })
        this.newItem = { title: '', description: '', genre: '', type: '', year: '', url: '', image: null }
        this.fetchItems(section)
      } catch (err) {
        this.handleAxiosError(err)
        console.error(`Failed to add ${section} item:`, err)
      }
    },

    async updateItem(section, item) {
      try {
        const token = localStorage.getItem('token')
        let payload = {}

        if (section === 'movies') {
          payload = {
            title: item.title,
            genre: item.genre,
            type: item.type,
            year: item.year ? parseInt(item.year) : null,
            url: item.url ? item.url : null,
          }
        } else {
          payload = {
            title: item.title,
            description: item.description,
          }
        }

        await axios.put(`/api/${section}/${item.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        })
      } catch (err) {
        this.handleAxiosError(err)
        console.error(`Failed to update ${section} item:`, err)
      }
    },

    async deleteItem(section, id) {
      if (!confirm('Are you sure you want to delete this item?')) return
      try {
        const token = localStorage.getItem('token')
        await axios.delete(`/api/${section}/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        this.fetchItems(section)
      } catch (err) {
        this.handleAxiosError(err)
        console.error(`Failed to delete ${section} item:`, err)
      }
    },

    async updateImage(event, section, item) {
      const file = event.target.files[0]
      if (!file) return

      const formData = new FormData()
      formData.append('image', file)

      try {
        const token = localStorage.getItem('token')
        await axios.put(`/api/${section}/${item.id}/image`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        })
        this.fetchItems(section)
      } catch (err) {
        this.handleAxiosError(err)
        console.error(`Failed to update image for ${section}:`, err)
      }
    },

    sectionLabel(key) {
      const s = this.sections.find(s => s.key === key)
      return s ? s.label : key
    },

    onFileChange(e) {
      this.newItem.image = e.target.files[0]
    },

    logout() {
      localStorage.removeItem('token')
      this.$router.push('/admin')
    },

    moveItem(section, index, direction) {
      const arr = this.items[section]
      const newIndex = index + direction
      if (newIndex < 0 || newIndex >= arr.length) return
      const temp = arr[index]
      arr.splice(index, 1)
      arr.splice(newIndex, 0, temp)
      this.onDragEnd(section)
    },

    async onDragEnd(section) {
      try {
        const token = localStorage.getItem('token')
        const orderedIds = this.items[section].map(item => item.id)
        await axios.put(`/api/${section}/reorder`, { orderedIds }, {
          headers: { Authorization: `Bearer ${token}` },
        })
        this.fetchItems(section)
      } catch (err) {
        this.handleAxiosError(err)
        console.error(`Failed to update order for ${section}`, err)
      }
    },
  },

  mounted() {
    this.sections.forEach(sec => { this.items[sec.key] = null })
    this.sections.forEach(sec => this.fetchItems(sec.key))
  },
}
</script>
